import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import parsePhoneNumberFromString from 'libphonenumber-js';
import * as mapbox from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { PointOfInterest } from '../../../types';
import { OwnerDashboardService } from '../../owner-dashboard.service';
import { Hazard, MigrationMappableDriver, MigrationMappableTarget } from './migration-map.service';

@Component({
  selector: 'lohi-migration-map',
  templateUrl: './migration-map.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MigrationMapComponent implements OnInit, AfterViewInit, OnDestroy {
  private pointOfInterests$: Observable<PointOfInterest[]>;

  @Input() set targets(targets: MigrationMappableTarget[]) {
    this.targets$$.next(targets || []);
  }

  @Input() set drivers(drivers: MigrationMappableDriver[]) {
    this.drivers$$.next(drivers || []);
  }

  @Input() set hazards(hazards: Hazard[]) {
    this.hazards$$.next(hazards || []);
  }

  @Input() public tourOnly = false;

  @Input() public driverMarkerRadius = 4;

  constructor(private ownerDashboardService: OwnerDashboardService) {
    (mapbox as any).accessToken =
      'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  }

  private map: mapbox.Map;
  private destroy$$ = new Subject();
  private targetMarkers: Record<number, mapbox.Marker> = {};
  private driverMarkers: Record<number, mapbox.Marker> = {};
  private targets$$ = new BehaviorSubject<MigrationMappableTarget[]>([]);
  private drivers$$ = new BehaviorSubject<MigrationMappableDriver[]>([]);
  private hazards$$ = new BehaviorSubject<Hazard[]>([]);
  public popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: true,
  });

  @ViewChildren('map') private mapElements: QueryList<ElementRef<HTMLDivElement>>;

  @Output() public targetClicked: EventEmitter<number> = new EventEmitter<number>();

  @Output() public mapClicked: EventEmitter<null> = new EventEmitter<null>();

  public ngOnInit(): void {
    this.pointOfInterests$ = this.ownerDashboardService.pointOfInterests$;
  }

  public ngAfterViewInit(): void {
    this.initMap();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  private async initMap() {
    // Make sure we have a map element
    if (this.mapElements.length === 0) {
      await this.mapElements.changes
        .pipe(
          filter((changes: QueryList<any>) => {
            return changes.length > 0;
          }),
          take(1),
        )
        .toPromise();
    }

    this.map = new mapboxgl.Map({
      container: this.mapElements.first.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
    });
    this.map.setZoom(3.5);
    this.map.setCenter({ lat: 39.8283, lng: -98.5795 });
    this.map.addControl(new mapbox.NavigationControl());
    this.map.addControl(new mapbox.FullscreenControl());
    this.map.loadImage('/assets/images/dollar.png', (error, image) => {
      this.map.addImage('targets', image);
    });
    this.map.loadImage('/assets/images/hazard-map.png', (error, image) => {
      this.map.addImage('hazards', image);
    });
    this.map.loadImage('/assets/images/1f.png', (error, image) => {
      this.map.addImage('1f', image);
    });

    this.map.loadImage('/assets/images/maintenance-shop.png', (error, image) => {
      this.map.addImage('maintenance-shop', image);
    });
    this.map.once('style.load', () => {
      this.listenToMapInfo();
    });
    this.map.on('click', (e) => {
      this.mapClicked.emit();
    });
  }

  private listenToMapInfo() {
    combineLatest([this.targets$$, this.drivers$$, this.hazards$$, this.pointOfInterests$])
      .pipe(takeUntil(this.destroy$$), debounceTime(200))
      .subscribe(([targets, drivers, hazards, pointOfInterests]) => {
        if (!targets || !drivers) {
          Object.values(this.targetMarkers).forEach((marker) => marker.remove());
          Object.values(this.driverMarkers).forEach((marker) => marker.remove());
          this.targetMarkers = {};
          this.driverMarkers = {};
          return;
        }
        if (!this.tourOnly) {
          if (pointOfInterests) {
            pointOfInterests = pointOfInterests.filter((data) => data.displayOnMap === true);
            this.updateMapPointOfInterestsSource(pointOfInterests);
          }
          this.updateMapHazardSource(hazards);
          this.updateMapTargetSource(targets);
        }
        this.updateMapDriverSource(drivers);
        this.recalculateMap(drivers);
      });
  }

  private updateMapDriverSource(drivers: MigrationMappableDriver[]) {
    const driversOnLoad = [];
    const driversOnShift = [];
    const driversOffShift = [];
    for (const driver of drivers) {
      if (driver.loadId) {
        driversOnLoad.push(driver);
      } else if (driver.onShift) {
        driversOnShift.push(driver);
      } else {
        driversOffShift.push(driver);
      }
    }
    const driverOnLoadSource = getDriverSource(driversOnLoad);
    const driverOnShiftSource = getDriverSource(driversOnShift);
    const driverOffShiftSource = getDriverSource(driversOffShift);

    if (
      this.map.getLayer('driversOnLoad') ||
      this.map.getLayer('driversOnShift') ||
      this.map.getLayer('driversOffShift')
    ) {
      (this.map.getSource('driversOnLoad') as mapbox.GeoJSONSource).setData(driverOnLoadSource.data);
      (this.map.getSource('driversOnShift') as mapbox.GeoJSONSource).setData(driverOnShiftSource.data);
      (this.map.getSource('driversOffShift') as mapbox.GeoJSONSource).setData(driverOffShiftSource.data);
    } else {
      this.map.addSource('driversOnLoad', driverOnLoadSource);
      this.map.addSource('driversOnShift', driverOnShiftSource);
      this.map.addSource('driversOffShift', driverOffShiftSource);
      this.map.addLayer({
        id: `driversOnLoad`,
        type: 'circle',
        source: `driversOnLoad`,
        paint: {
          'circle-radius': this.driverMarkerRadius,
          'circle-color': '#0096FF',
          'circle-stroke-color': 'white',
          'circle-stroke-width': 1,
        },
      });
      this.map.addLayer({
        id: `driversOnShift`,
        type: 'circle',
        source: `driversOnShift`,
        paint: {
          'circle-radius': this.driverMarkerRadius,
          'circle-color': '#000000',
          'circle-stroke-color': 'white',
          'circle-stroke-width': 1,
        },
      });
      this.map.addLayer({
        id: `driversOffShift`,
        type: 'circle',
        source: `driversOffShift`,
        paint: {
          'circle-radius': this.driverMarkerRadius,
          'circle-color': '#808080',
          'circle-stroke-color': 'white',
          'circle-stroke-width': 1,
        },
      });
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
      });
      this.map.on('mouseenter', 'driversOnLoad', (e) => {
        this.map.getCanvas().style.cursor = 'pointer';
        popup.setLngLat(e.lngLat);
        popup.setHTML(e.features[0].properties.description);
        popup.addTo(this.map);
      });
      this.map.on('mouseenter', 'driversOnShift', (e) => {
        this.map.getCanvas().style.cursor = 'pointer';
        popup.setLngLat(e.lngLat);
        popup.setHTML(e.features[0].properties.description);
        popup.addTo(this.map);
      });
      this.map.on('mouseenter', 'driversOffShift', (e) => {
        this.map.getCanvas().style.cursor = 'pointer';
        popup.setLngLat(e.lngLat);
        popup.setHTML(e.features[0].properties.description);
        popup.addTo(this.map);
      });
      this.map.on('mouseleave', 'driversOnLoad', () => {
        this.map.getCanvas().style.cursor = '';
        popup.remove();
      });
      this.map.on('mouseleave', 'driversOnShift', () => {
        this.map.getCanvas().style.cursor = '';
        popup.remove();
      });
      this.map.on('mouseleave', 'driversOffShift', () => {
        this.map.getCanvas().style.cursor = '';
        popup.remove();
      });
    }
  }

  private updateMapTargetSource(targets: MigrationMappableTarget[]) {
    const targetSource = getTargetSource(targets);
    if (this.map.getLayer('targets')) {
      (this.map.getSource('targets') as mapbox.GeoJSONSource).setData(targetSource.data);
    } else {
      this.map.addSource('targets', targetSource);
      this.map.addLayer({
        id: `targets`,
        type: 'symbol',
        source: `targets`,
        layout: {
          'icon-image': 'targets',
          'icon-size': 1.2,
          'icon-allow-overlap': true,
        },
      });
      this.map.on('click', `targets`, (e) => {
        this.targetClicked.emit(e.features[0].properties.id);
        new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
        })
          .setLngLat(e.lngLat)
          .setHTML(e.features[0].properties.description)
          .addTo(this.map);
      });
    }
  }

  private updateMapPointOfInterestsSource(pointOfInterests: PointOfInterest[]) {
    const pointOfInterestsSource = getPointOfInterestsSource(pointOfInterests);
    if (this.map.getLayer('pointOfInterests')) {
      (this.map.getSource('pointOfInterests') as mapbox.GeoJSONSource).setData(pointOfInterestsSource.data);
    } else {
      this.map.addSource('pointOfInterests', pointOfInterestsSource);
      this.map.addLayer({
        id: `pointOfInterests`,
        type: 'symbol',
        source: `pointOfInterests`,
        layout: {
          'icon-image': [
            'match',
            ['get', 'type'],
            '1f_yard',
            '1f',
            'maintenance_shop',
            'maintenance-shop',
            /* other */ '1f',
          ],
          'icon-size': 0.8,
          'icon-allow-overlap': true,
        },
      });
      this.map.on('click', `pointOfInterests`, (e) => {
        new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
        })
          .setLngLat(e.lngLat)
          .setHTML(e.features[0].properties.description)
          .addTo(this.map);
      });
    }
  }

  private updateMapHazardSource(hazards: Hazard[]) {
    const hazardSource = getHazardSource(hazards);
    if (this.map.getLayer('hazards')) {
      (this.map.getSource('hazards') as mapbox.GeoJSONSource).setData(hazardSource.data);
    } else {
      this.map.addSource('hazards', hazardSource);
      this.map.addLayer({
        id: `hazards`,
        type: 'symbol',
        source: `hazards`,
        layout: {
          'icon-image': 'hazards',
          'icon-size': 0.15,
          'icon-allow-overlap': true,
        },
      });
      this.map.on('click', `hazards`, (e) => {
        this.map.getCanvas().style.cursor = 'pointer';
        new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: true,
        })
          .setLngLat(e.lngLat)
          .setHTML(e.features[0].properties.description)
          .addTo(this.map);
      });
    }
  }

  private recalculateMap(drivers: MigrationMappableDriver[]) {
    if (drivers.length !== 1) {
      this.popup.remove();
    }
    if (drivers.length === 1) {
      this.map.setZoom(10);
      this.map.setCenter({
        lat: drivers[0].lastWaypointLnglat.latitude,
        lng: drivers[0].lastWaypointLnglat.longitude,
      });
      this.popup
        .setLngLat([drivers[0].lastWaypointLnglat.longitude, drivers[0].lastWaypointLnglat.latitude])
        .setHTML(
          drivers[0].onTour || this.tourOnly
            ? `<strong>${drivers[0].name}</strong>
           <p style="margin-bottom:0; font-size: 12px;">${drivers[0].onTour ? 'On Tour' : 'Off Tour'}</p>`
            : `<strong>${drivers[0].name}</strong>
           <p style="margin-bottom:0; font-size: 12px;">${drivers[0].onShift ? 'On Shift' : 'Off Shift'} / ${
                drivers[0].onHitch ? 'On Hitch' : 'Off Hitch'
              }</p>
           <p style="margin-bottom:0; font-size: 12px;">Updated ${formatDate(drivers[0].lastWaypointTime)}</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/phone.png"/>${formatPhoneNumber(
             drivers[0].phone,
           )}</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/truck.png"/>${
             drivers[0].truckName ? drivers[0].truckName : 'N/A'
           }</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/trailer.png"/>${
             drivers[0].trailerName ? drivers[0].trailerName : 'N/A'
           }</p>
           <p style="margin-bottom:0; font-size: 12px;">${
             drivers[0].loadId ? 'On Load ' + drivers[0].loadId : 'Not on Load'
           }</p>`,
        )
        .addTo(this.map);
      this.map.on('mouseenter', 'driversOnLoad', () => {
        this.map.getCanvas().style.cursor = '';
        this.popup.remove();
      });
      this.map.on('mouseenter', 'driversOnShift', () => {
        this.map.getCanvas().style.cursor = '';
        this.popup.remove();
      });
      this.map.on('mouseenter', 'driversOffShift', () => {
        this.map.getCanvas().style.cursor = '';
        this.popup.remove();
      });
    }
  }
}

function getDriverSource(drivers: MigrationMappableDriver[]): mapboxgl.GeoJSONSourceRaw {
  const driverData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: drivers.map((driver) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [driver.lastWaypointLnglat.longitude, driver.lastWaypointLnglat.latitude],
      },
      properties: {
        id: driver.name,
        description: `<strong>${driver.name}</strong>
           <p style="margin-bottom:0; font-size: 12px;">${driver.onShift ? 'On Shift' : 'Off Shift'} / ${
          driver.onHitch ? 'On Hitch' : 'Off Hitch'
        }</p>
           <p style="margin-bottom:0; font-size: 12px;">Updated ${formatDate(driver.lastWaypointTime)}</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/phone.png"/>${formatPhoneNumber(
             driver.phone,
           )}</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/truck.png"/>${
             driver.truckName ? driver.truckName : 'N/A'
           }</p>
           <p style="margin-bottom:0; font-size: 12px; display: flex; align-items: center;"><img class="w-2 h-2 mr-1" src="assets/images/trailer.png"/>${
             driver.trailerName ? driver.trailerName : 'N/A'
           }</p>
           <p style="margin-bottom:0; font-size: 12px;">${
             driver.loadId ? 'On Load ' + driver.loadId : 'Not on Load'
           }</p>`,
      },
    })),
  };
  const driverSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: driverData,
  };

  return driverSource;
}

function getTargetSource(targets: MigrationMappableTarget[]): mapboxgl.GeoJSONSourceRaw {
  const targetData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: targets.map((target) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [target.lng, target.lat],
      },
      properties: {
        id: target.sandDemandSnapshotId,
        description: `<strong>${target.name}</strong>
                      <p style="margin-bottom:0; font-size: 10px;">Start By: ${
                        target.startDate
                          ? target.startDate.month +
                            '/' +
                            target.startDate.day +
                            '/' +
                            target.startDate.year +
                            '  |  Next ' +
                            target.durationDays +
                            ' days'
                          : 'ASAP'
                      }</p>
                      <p style="margin-bottom:0; font-size: 10px;">Revenue: ${
                        ' $' + target.estimatedCentsPerShift / 100 + '/shift'
                      }</p>`,
      },
    })),
  };
  const targetSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: targetData,
  };

  return targetSource;
}

function getPointOfInterestsSource(pointOfInterests: PointOfInterest[]): mapboxgl.GeoJSONSourceRaw {
  const pointOfInterestsData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: pointOfInterests.map((pointOfInterest) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [pointOfInterest.lnglat.longitude, pointOfInterest.lnglat.latitude],
      },
      properties: {
        id: pointOfInterest.id,
        type: pointOfInterest.type,
        description: `<strong>${pointOfInterest.name}</strong>
<p style="margin-bottom:0; font-size: 12px;" class="text-blue-500">${pointOfInterest.phone}</p>
<p style="margin-bottom:0; font-size: 12px;">${pointOfInterest.address}</p>
<p style="margin-bottom:0; font-size: 12px;">${pointOfInterest.description}</p>
                      `,
      },
    })),
  };
  return {
    type: 'geojson',
    data: pointOfInterestsData,
  };
}

function getHazardSource(hazards: Hazard[]): mapboxgl.GeoJSONSourceRaw {
  const hazardData: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: 'FeatureCollection',
    features: hazards.map((hazard) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [hazard.location.longitude, hazard.location.latitude],
      },
      properties: {
        id: hazard.id,
        description: `<strong>${hazard.groupDisplayName}</strong>
                      <p style="margin-bottom:0">Reported By: ${hazard.reportedByName}</p>
                      <p style="margin-bottom:0">Trucks in Line: ${
                        hazard.trucksInLine ? hazard.trucksInLine.replace(/_/g, ' ') : ''
                      }</p>
                      <p style="margin-bottom:0">Position: ${
                        hazard.positionInLine ? hazard.positionInLine.replace(/_/g, ' ') : ''
                      }</p>
                      <p style="margin-bottom:0">Last Updated: ${
                        hazard.updatedAt ? formatDate(hazard.updatedAt) : ''
                      }</p>`,
      },
    })),
  };
  const hazardSource: mapboxgl.GeoJSONSourceRaw = {
    type: 'geojson',
    data: hazardData,
  };

  return hazardSource;
}

function formatPhoneNumber(phoneNumber) {
  return parsePhoneNumberFromString(phoneNumber).formatNational();
}

// calculates the amount of time passed
function formatDate(date) {
  date = new Date(date);
  const seconds = Math.floor((new Date().getTime() - date) / 1000);
  let interval = seconds / 31536000;

  if (interval >= 1) {
    if (Math.floor(interval) === 1) {
      return Math.floor(interval) + ' year ago';
    }
    return Math.floor(interval) + ' years ago';
  }
  interval = seconds / 2592000;
  if (interval >= 1) {
    if (Math.floor(interval) === 1) {
      return Math.floor(interval) + ' month ago';
    }
    return Math.floor(interval) + ' months ago';
  }
  interval = seconds / 86400;
  if (interval >= 1) {
    if (Math.floor(interval) === 1) {
      return Math.floor(interval) + ' day ago';
    }
    return Math.floor(interval) + ' days ago';
  }
  interval = seconds / 3600;
  if (interval >= 1) {
    if (Math.floor(interval) === 1) {
      return Math.floor(interval) + ' hour ago';
    }
    return Math.floor(interval) + ' hours ago';
  }
  interval = seconds / 60;
  if (interval >= 1) {
    if (Math.floor(interval) === 1) {
      return Math.floor(interval) + ' minute ago';
    }
    return Math.floor(interval) + ' minutes ago';
  }
  return Math.floor(seconds) + ' seconds';
}
